/**
 * The plate as an SVG string, from the glyph paths and the legal layout. One renderer for the
 * web component and anything server-side that can show SVG. Only glyph data and numbers reach
 * the markup: the reg itself is never interpolated, so the string is safe to inject.
 */
import { GLYPH_HEIGHT, PLATE_GLYPHS, PLATE_STROKE } from './glyphs';
import { layoutPlate, PLATE_RADIUS, type PlateLayout } from './layout';

export type PlateVariant = 'rear' | 'front';

export interface PlateSvgOptions {
  /** rear = reflective yellow (default); front = white. */
  variant?: PlateVariant;
  /** Blue UK identifier band on the left. */
  band?: boolean;
  /** Union flag above "UK" in the band. */
  flag?: boolean;
  /**
   * Fine detail: the inset black border line, the bevel and the BS mark. Off for small plates,
   * where a 2 mm line would be a blurred sub-pixel smear.
   */
  detail?: boolean;
  /** Prefix for ids in <defs>; must be unique on the page (React useId). */
  idPrefix?: string;
  /** Rendered height in px, written as height=; width follows the aspect ratio. */
  height?: number;
}

/** Colours as found on real plates (reflective sheeting, not flat CSS yellow). */
export const PLATE_COLOURS = {
  rear: { top: '#FFDC3C', mid: '#F9D013', bottom: '#EDBF00', base: '#F8CF14' },
  front: { top: '#FFFFFF', mid: '#FAFAF8', bottom: '#ECECE8', base: '#F8F8F6' },
  ink: '#121212',
  band: { top: '#1F4FB4', bottom: '#00308F', base: '#003399' },
} as const;

const UNION_FLAG =
  '<path d="M0 0v30h60V0z" fill="#012169"/>' +
  '<path d="M0 0l60 30M60 0L0 30" stroke="#fff" stroke-width="6"/>' +
  '<path d="M0 0l60 30M60 0L0 30" stroke="#C8102E" stroke-width="2.4" clip-path="url(#ID-ft)"/>' +
  '<path d="M30 0v30M0 15h60" stroke="#fff" stroke-width="10"/>' +
  '<path d="M30 0v30M0 15h60" stroke="#C8102E" stroke-width="6"/>';

function n(v: number): string {
  return String(Math.round(v * 100) / 100);
}

function glyphPath(ch: string): string {
  return PLATE_GLYPHS[ch]!.strokes.join(' ');
}

/** Characters placed by the layout, each clipped to its own box. */
function glyphMarkup(layout: PlateLayout, id: string): string {
  return layout.chars
    .map(
      (c) =>
        `<path transform="translate(${n(c.x)} ${n(c.y)})" clip-path="url(#${id}-c${c.width})" d="${glyphPath(c.ch)}"/>`,
    )
    .join('');
}

function clipDefs(widths: Iterable<number>, id: string): string {
  return [...new Set(widths)]
    .map(
      (w) =>
        `<clipPath id="${id}-c${w}" clipPathUnits="userSpaceOnUse"><rect width="${w}" height="${GLYPH_HEIGHT}"/></clipPath>`,
    )
    .join('');
}

/** "UK" in the band, in the plate's own letterforms at ~30% scale. */
function bandMarkup(bandWidth: number, height: number, id: string, flag: boolean): string {
  const s = 0.29;
  const letterW = 50 * s;
  const gap = 3.2;
  const total = letterW * 2 + gap;
  const x = (bandWidth - total) / 2;
  const y = flag ? height - 14 - GLYPH_HEIGHT * s : (height - GLYPH_HEIGHT * s) / 2;
  const letters = ['U', 'K']
    .map(
      (ch, i) =>
        `<path transform="translate(${n(x + i * (letterW + gap))} ${n(y)}) scale(${s})" clip-path="url(#${id}-c50)" d="${glyphPath(ch)}"/>`,
    )
    .join('');
  const flagW = 34;
  const flagMarkup = flag
    ? `<g transform="translate(${n((bandWidth - flagW) / 2)} 15) scale(${flagW / 60})" clip-path="url(#${id}-fs)">${UNION_FLAG.replaceAll('ID', id)}</g>`
    : '';
  return (
    `<rect width="${bandWidth}" height="${height}" fill="url(#${id}-band)"/>` +
    flagMarkup +
    `<g fill="none" stroke="#fff" stroke-width="${PLATE_STROKE}" stroke-miterlimit="10">${letters}</g>`
  );
}

export function renderPlateSvg(reg: string, opts: PlateSvgOptions = {}): string {
  const variant = opts.variant ?? 'rear';
  const band = opts.band ?? false;
  const flag = band && (opts.flag ?? true);
  const detail = opts.detail ?? true;
  const id = (opts.idPrefix ?? 'plate').replace(/[^A-Za-z0-9_-]/g, '');
  const layout = layoutPlate(reg, { band });
  const { width: w, height: h, bandWidth } = layout;
  const c = PLATE_COLOURS[variant];
  const rad = PLATE_RADIUS;
  const size =
    opts.height !== undefined
      ? ` height="${n(opts.height)}" width="${n((opts.height * w) / h)}"`
      : '';

  const widths = layout.chars.map((ch) => ch.width);
  if (band) widths.push(50);

  const defs =
    `<linearGradient id="${id}-bg" x1="0" y1="0" x2="0" y2="1">` +
    `<stop offset="0" stop-color="${c.top}"/><stop offset=".55" stop-color="${c.mid}"/><stop offset="1" stop-color="${c.bottom}"/>` +
    `</linearGradient>` +
    // A soft diagonal sheen: the reflective film catches light across the top-left.
    `<linearGradient id="${id}-sheen" x1="0" y1="0" x2=".55" y2="1">` +
    `<stop offset="0" stop-color="#fff" stop-opacity=".32"/><stop offset=".38" stop-color="#fff" stop-opacity=".08"/>` +
    `<stop offset=".52" stop-color="#fff" stop-opacity="0"/><stop offset="1" stop-color="#000" stop-opacity=".05"/>` +
    `</linearGradient>` +
    `<linearGradient id="${id}-bevel" x1="0" y1="0" x2="0" y2="1">` +
    `<stop offset="0" stop-color="#fff" stop-opacity=".75"/><stop offset=".5" stop-color="#fff" stop-opacity="0"/>` +
    `<stop offset=".51" stop-color="#000" stop-opacity="0"/><stop offset="1" stop-color="#000" stop-opacity=".22"/>` +
    `</linearGradient>` +
    (band
      ? `<linearGradient id="${id}-band" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="${PLATE_COLOURS.band.top}"/><stop offset="1" stop-color="${PLATE_COLOURS.band.bottom}"/></linearGradient>`
      : '') +
    (flag
      ? `<clipPath id="${id}-fs"><path d="M0 0v30h60V0z"/></clipPath>` +
        `<clipPath id="${id}-ft"><path d="M30 15h30v15zv15H0zH0V0zV0h30z"/></clipPath>`
      : '') +
    `<clipPath id="${id}-plate"><rect width="${n(w)}" height="${h}" rx="${rad}"/></clipPath>` +
    clipDefs(widths, id);

  const inset = 4.5;
  const border = detail
    ? `<rect x="${n(bandWidth + inset)}" y="${inset}" width="${n(w - bandWidth - inset * 2)}" height="${h - inset * 2}" rx="${rad - inset + 1}" fill="none" stroke="${PLATE_COLOURS.ink}" stroke-width="2.2"/>`
    : '';
  const bevel = detail
    ? `<rect x="1.2" y="1.2" width="${n(w - 2.4)}" height="${h - 2.4}" rx="${rad - 1.2}" fill="none" stroke="url(#${id}-bevel)" stroke-width="2.4"/>`
    : '';
  const mark = detail
    ? `<text x="${n(w - 22)}" y="${h - 6.4}" text-anchor="end" font-family="Arial Narrow,Arial,Helvetica,sans-serif" font-size="5" fill="${PLATE_COLOURS.ink}" fill-opacity=".8">BS AU 145e</text>`
    : '';

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${n(w)} ${h}"${size} aria-hidden="true" focusable="false">` +
    `<defs>${defs}</defs>` +
    `<g clip-path="url(#${id}-plate)">` +
    `<rect width="${n(w)}" height="${h}" fill="${c.base}"/>` +
    `<rect width="${n(w)}" height="${h}" fill="url(#${id}-bg)"/>` +
    (band ? bandMarkup(bandWidth, h, id, flag) : '') +
    border +
    mark +
    `<g fill="none" stroke="${PLATE_COLOURS.ink}" stroke-width="${PLATE_STROKE}" stroke-linejoin="miter" stroke-miterlimit="10">${glyphMarkup(layout, id)}</g>` +
    `<rect width="${n(w)}" height="${h}" fill="url(#${id}-sheen)"/>` +
    bevel +
    `</g>` +
    `<rect x=".5" y=".5" width="${n(w - 1)}" height="${h - 1}" rx="${rad - 0.5}" fill="none" stroke="#000" stroke-opacity=".5" stroke-width="1" vector-effect="non-scaling-stroke"/>` +
    `</svg>`
  );
}
